import { useLocalStorage } from "react-use";
import { type PageSize, INITIAL_PAGE_SIZE } from "@/components/Pagination/usePagination";

const PAGE_SIZE_STORAGE_KEY_PREFIX = "table-pageSize-";

/**
 * Utility function to read page size preference from localStorage.
 * Can be used outside React components (e.g., in route loaders).
 *
 * @param tableId - The unique identifier for the table (e.g., "bookingsTable")
 * @returns The stored page size preference, or null if not found or invalid
 */
export function getStoredPageSize(tableId: string): PageSize | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(`${PAGE_SIZE_STORAGE_KEY_PREFIX}${tableId}`);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return ["10", "20", "50", "100"].includes(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Hook to manage page size preference in localStorage per table.
 * Stores preference as `table-pageSize-${tableId}` (following the pattern used by other table settings).
 *
 * @param tableId - The unique identifier for the table (e.g., "bookingsTable")
 * @returns Tuple of [pageSize, setPageSize]
 * - pageSize: Current page size preference for the table
 * - setPageSize: Function to update the page size preference
 *
 * @example
 * ```tsx
 * const [preferredPageSize, setPreferredPageSize] = usePageSizePreference("bookingsTable");
 * ```
 */
export function usePageSizePreference(tableId: string): [PageSize, (pageSize: PageSize) => void] {
  const [storedPageSize, setStoredPageSize] = useLocalStorage<PageSize>(
    `${PAGE_SIZE_STORAGE_KEY_PREFIX}${tableId}`,
    INITIAL_PAGE_SIZE,
  );

  const pageSize = storedPageSize || INITIAL_PAGE_SIZE;

  const setPageSize = (newPageSize: PageSize) => {
    setStoredPageSize(newPageSize);
  };

  return [pageSize, setPageSize];
}
